import { useState, useEffect, useRef } from 'react'
import { motion } from 'framer-motion'
import AnimatedHeading from '@/Components/UI/AnimatedHeading'
import { Status, Wrapper } from '@googlemaps/react-wrapper'
import { GOOGLE_MAPS_API_KEY, GOOGLE_MAPS_LIBRARIES } from '@/Constants'
import LocationSearchBox from '@/Views/FormSteps/Location/Base/SearchBox/SearchBox'
import fetchLocationByPlaceId from '@/Utilities/Location/fetchLocationByPlaceId'
import fetchLocationByCoordinates from '@/Utilities/Location/fetchLocationByCoordinates'
import LocationMapWithDrawing, {
  PolygonCoord,
  VenuePin,
  computeCentroid,
} from './LocationMapWithDrawing'
import haversine from 'haversine-distance'

export type LocationMode = 'radius' | 'polygon'

export interface LocationSelectionData {
  location: string
  lat: number
  lon: number
  country: string
  leadFeedCity?: string
  mode: LocationMode
  radiusKm: number
  polygonCoordinates?: PolygonCoord[]
}

interface LocationSectionProps {
  onChange: (data: LocationSelectionData | null) => void
  initialData?: LocationSelectionData
  venuePins?: VenuePin[]
  hideHeading?: boolean
  fullWidth?: boolean
}

const MIN_RADIUS = 1
const MAX_RADIUS = 10
const DEFAULT_RADIUS = 3

const LocationSectionInner = ({ onChange, initialData, venuePins }: LocationSectionProps) => {
  const [locationAttrs, setLocationAttrs] = useState<{
    location: string
    lat: number
    lon: number
    country: string
    leadFeedCity?: string
  } | null>(
    initialData
      ? {
          location: initialData.location,
          lat: initialData.lat,
          lon: initialData.lon,
          country: initialData.country,
          leadFeedCity: initialData.leadFeedCity,
        }
      : null
  )

  const [mode, setMode] = useState<LocationMode>(initialData?.mode ?? 'radius')
  const [radiusKm, setRadiusKm] = useState(initialData?.radiusKm ?? DEFAULT_RADIUS)
  const [polygonCoordinates, setPolygonCoordinates] = useState<PolygonCoord[] | null>(
    initialData?.polygonCoordinates ?? null
  )

  const placesServiceRef = useRef<google.maps.places.PlacesService | null>(null)
  const polygonGeocodeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    const div = document.createElement('div')
    placesServiceRef.current = new google.maps.places.PlacesService(div)
    return () => {
      if (polygonGeocodeTimerRef.current) clearTimeout(polygonGeocodeTimerRef.current)
    }
  }, [])

  useEffect(() => {
    if (!venuePins?.length) return

    const centroid = computeCentroid(venuePins)
    const maxDistKm = Math.max(...venuePins.map((p) => haversine(centroid, p) / 1000))
    const minRadius = Math.min(MAX_RADIUS, Math.max(MIN_RADIUS, Math.ceil(maxDistKm * 1.2 * 2) / 2))

    setRadiusKm((prev) => Math.max(prev, minRadius))

    if (!locationAttrs) {
      const name = venuePins.length === 1 ? venuePins[0].name : ''
      setLocationAttrs({ location: name, lat: centroid.lat, lon: centroid.lng, country: '' })
    }
  }, [venuePins])

  useEffect(() => {
    if (!venuePins?.length || locationAttrs?.country) return

    const centroid = computeCentroid(venuePins)
    let cancelled = false
    fetchLocationByCoordinates({ lat: centroid.lat, lon: centroid.lng })
      .then((attrs) => {
        if (cancelled) return
        setLocationAttrs((prev) =>
          prev?.country
            ? prev
            : {
                location: prev?.location || attrs.location,
                lat: prev?.lat ?? centroid.lat,
                lon: prev?.lon ?? centroid.lng,
                country: attrs.country ?? '',
                leadFeedCity: attrs.leadFeedCity ?? undefined,
              }
        )
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [venuePins])

  useEffect(() => {
    if (!locationAttrs) {
      onChange(null)
      return
    }
    onChange({
      ...locationAttrs,
      mode,
      radiusKm,
      polygonCoordinates: mode === 'polygon' ? polygonCoordinates ?? undefined : undefined,
    })
  }, [locationAttrs, mode, radiusKm, polygonCoordinates])

  const handlePlaceSelect = async (placeId: string) => {
    if (!placeId || !placesServiceRef.current) return
    const attrs = await fetchLocationByPlaceId({
      placeId,
      placesService: placesServiceRef.current,
    })
    if (attrs) {
      setLocationAttrs({
        location: attrs.location,
        lat: attrs.lat,
        lon: attrs.lon,
        country: attrs.country ?? '',
        leadFeedCity: attrs.leadFeedCity ?? undefined,
      })
    }
  }

  const handlePolygonChange = (coords: PolygonCoord[] | null) => {
    setPolygonCoordinates(coords)

    if (polygonGeocodeTimerRef.current) clearTimeout(polygonGeocodeTimerRef.current)
    if (!coords?.length) return

    polygonGeocodeTimerRef.current = setTimeout(async () => {
      const centroid = computeCentroid(coords)
      try {
        const attrs = await fetchLocationByCoordinates({ lat: centroid.lat, lon: centroid.lng })
        setLocationAttrs({
          location: attrs.location,
          lat: attrs.lat,
          lon: attrs.lon,
          country: attrs.country ?? '',
          leadFeedCity: attrs.leadFeedCity ?? undefined,
        })
      } catch {
        // keep the previous location if reverse geocoding fails
      }
    }, 500)
  }

  const center = locationAttrs ? { lat: locationAttrs.lat, lon: locationAttrs.lon } : undefined

  return (
    <div className='bg-white border border-[#d9d9d9] rounded-[4px] px-4 py-6 flex flex-col justify-center gap-6'>
      <LocationSearchBox
        id='duplicate-form-location-search'
        location={locationAttrs?.location ?? ''}
        setLocation={handlePlaceSelect}
      />

      <div className='flex gap-6 items-center justify-center'>
        <label className='flex gap-2 items-center cursor-pointer select-none'>
          <input
            type='checkbox'
            className='w-4 h-4 accent-[#1270B8] cursor-pointer'
            checked={mode === 'radius'}
            onChange={() => setMode('radius')}
          />
          <span className='font-[Montserrat] text-base text-[#1f1f1f] leading-6'>Use radius</span>
        </label>
        <label className='flex gap-2 items-center cursor-pointer select-none'>
          <input
            type='checkbox'
            className='w-4 h-4 accent-[#1270B8] cursor-pointer'
            checked={mode === 'polygon'}
            onChange={() => setMode('polygon')}
          />
          <span className='font-[Montserrat] text-base text-[#1f1f1f] leading-6'>Draw polygon</span>
        </label>
      </div>

      {mode === 'radius' && (
        <div className='flex flex-col items-center gap-1'>
          <div className='flex gap-4 items-center w-full'>
            <span className='font-[Montserrat] text-sm text-black tracking-[0.07px] whitespace-nowrap'>
              {MIN_RADIUS}km
            </span>
            <input
              type='range'
              min={MIN_RADIUS}
              max={MAX_RADIUS}
              step={0.5}
              value={radiusKm}
              onChange={(e) => setRadiusKm(Number(e.target.value))}
              className='flex-1 accent-[#AF231C] cursor-pointer'
            />
            <span className='font-[Montserrat] text-sm text-black tracking-[0.07px] whitespace-nowrap'>
              {MAX_RADIUS}km
            </span>
          </div>
          <span className='font-[Montserrat] text-sm text-black tracking-[0.07px]'>
            {radiusKm}km
          </span>
        </div>
      )}

      {mode === 'polygon' && (
        <p className='flex justify-center'>
          Click on the map to start drawing. You need at least 3 points to create a shape.
        </p>
      )}

      <div className='rounded-[4px] overflow-hidden'>
        <LocationMapWithDrawing
          center={center}
          mode={mode}
          radiusKm={radiusKm}
          onPolygonChange={handlePolygonChange}
          existingPolygon={polygonCoordinates ?? undefined}
          venuePins={venuePins}
        />
      </div>
    </div>
  )
}

const LocationSection = ({ hideHeading, fullWidth, ...props }: LocationSectionProps) => {
  const render = (status: Status) => {
    if (status === Status.LOADING)
      return <div className='h-[300px] bg-gray-50 rounded-[4px] animate-pulse' />
    if (status === Status.FAILURE)
      return (
        <div className='h-[300px] bg-gray-50 rounded-[4px] flex items-center justify-center'>
          <p className='text-sm text-gray-500'>Maps failed to load.</p>
        </div>
      )
    return <LocationSectionInner {...props} />
  }

  return (
    <div className={fullWidth ? 'w-full' : 'max-w-3xl mx-auto'}>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}>
        {!hideHeading && (
          <div className='flex flex-col gap-3 mb-6'>
            <AnimatedHeading size='large'>Do you have a preferred location?</AnimatedHeading>
            <p
              className='font-bold !text-md sm:!text-lg tracking-[-0.2px]'
              style={{
                background: 'linear-gradient(90deg, #000 0%, #AF231C 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}>
              Add a general radius or use the polygon tool to be more specific.
            </p>
          </div>
        )}
        <Wrapper apiKey={GOOGLE_MAPS_API_KEY} libraries={GOOGLE_MAPS_LIBRARIES} render={render} />
      </motion.div>
    </div>
  )
}

export default LocationSection
